import * as React from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Alignment options for dropdown content positioning
 */
export type DropdownAlign = 'start' | 'center' | 'end';

/**
 * Side options for dropdown content positioning
 */
export type DropdownSide = 'top' | 'bottom';

/**
 * Props for the main DropdownMenu component
 */
export interface DropdownMenuProps {
  /**
   * Children should include DropdownMenu.Trigger and DropdownMenu.Content
   */
  children: React.ReactNode;
}

/**
 * Props for DropdownMenu.Trigger
 */
export interface DropdownTriggerProps {
  /**
   * Content of the trigger button
   */
  children: React.ReactNode;
  /**
   * Use provided child as trigger instead of wrapping in a button
   * @default false
   */
  asChild?: boolean;
  /**
   * Additional CSS classes for the trigger button
   */
  className?: string;
}

/**
 * Props for DropdownMenu.Content
 */
export interface DropdownContentProps {
  /**
   * Menu items (DropdownMenu.Item, DropdownMenu.Divider)
   */
  children: React.ReactNode;
  /**
   * Horizontal alignment relative to trigger
   * @default 'start'
   */
  align?: DropdownAlign;
  /**
   * Side to open the dropdown
   * @default 'bottom'
   */
  side?: DropdownSide;
  /**
   * Additional CSS classes for the content container
   */
  className?: string;
}

/**
 * Props for DropdownMenu.Item
 */
export interface DropdownItemProps {
  /**
   * Item label content
   */
  children: React.ReactNode;
  /**
   * Icon to display on the left side
   */
  icon?: React.ReactNode;
  /**
   * Callback when item is selected
   */
  onSelect?: () => void;
  /**
   * Whether the item is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the item is a destructive action (red color)
   * @default false
   */
  destructive?: boolean;
  /**
   * Whether to close the menu when this item is selected.
   * Overrides the default behavior (close on select).
   * Set to false to keep the menu open after selection.
   * @default true
   */
  closeOnSelect?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for DropdownMenu.Divider
 */
export interface DropdownDividerProps {
  /**
   * Additional CSS classes
   */
  className?: string;
}

// Context for passing dropdown state to compound components
interface DropdownContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  closeOnSelect: boolean;
  menuId: string;
}

const DropdownContext = React.createContext<DropdownContextValue | null>(null);

function useDropdownContext(): DropdownContextValue {
  const context = React.useContext(DropdownContext);
  if (!context) {
    throw new Error('DropdownMenu compound components must be used within DropdownMenu');
  }
  return context;
}

// Generate unique ID for accessibility
let dropdownIdCounter = 0;
function generateDropdownId(): string {
  return `dropdown-${Date.now()}-${++dropdownIdCounter}`;
}

/**
 * DropdownMenu Trigger component - renders the button that opens the menu
 */
function DropdownTrigger({
  children,
  asChild = false,
  className = '',
}: Readonly<DropdownTriggerProps>) {
  const { isOpen, setIsOpen, triggerRef, menuId } = useDropdownContext();

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'ArrowDown' && !isOpen) {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  const baseClasses = [
    'inline-flex items-center justify-center gap-2',
    'h-10 px-4',
    'text-sm font-medium',
    'text-secondary-700',
    'bg-secondary-50',
    'border border-secondary-300',
    'rounded-md',
    'transition-all duration-150 ease-out',
    'hover:bg-secondary-100',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ].join(' ');

  if (asChild && React.isValidElement(children)) {
    // Clone the child and add necessary props
    return React.cloneElement(children as React.ReactElement<{
      ref?: React.Ref<HTMLButtonElement>;
      onClick?: (event: React.MouseEvent) => void;
      onKeyDown?: (event: React.KeyboardEvent) => void;
      'aria-expanded'?: boolean;
      'aria-haspopup'?: 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | true | false;
      'aria-controls'?: string;
    }>, {
      ref: triggerRef,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      'aria-expanded': isOpen,
      'aria-haspopup': 'menu',
      'aria-controls': isOpen ? menuId : undefined,
    });
  }

  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-expanded={isOpen}
      aria-haspopup="menu"
      aria-controls={isOpen ? menuId : undefined}
      className={[baseClasses, className].filter(Boolean).join(' ')}
    >
      {children}
      <ChevronDown
        className={[
          'w-4 h-4',
          'transition-transform duration-150',
          isOpen ? 'rotate-180' : '',
        ].join(' ')}
        aria-hidden="true"
      />
    </button>
  );
}

DropdownTrigger.displayName = 'DropdownMenu.Trigger';

/**
 * DropdownMenu Content component - the dropdown panel containing items
 */
function DropdownContent({
  children,
  align = 'start',
  side = 'bottom',
  className = '',
}: Readonly<DropdownContentProps>) {
  const {
    isOpen,
    setIsOpen,
    triggerRef,
    contentRef,
    focusedIndex,
    setFocusedIndex,
    menuId,
  } = useDropdownContext();

  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [itemCount, setItemCount] = React.useState(0);

  // Count the actual items (not dividers)
  React.useEffect(() => {
    if (contentRef.current) {
      const items = contentRef.current.querySelectorAll('[role="menuitem"]');
      setItemCount(items.length);
      itemRefs.current = Array.from(items) as HTMLDivElement[];
    }
  }, [isOpen, children, contentRef]);

  // Focus the appropriate item when index changes
  React.useEffect(() => {
    if (isOpen && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, isOpen]);

  // Handle click outside
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        contentRef.current &&
        !contentRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen, contentRef, triggerRef]);

  // Handle escape key and keyboard navigation
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex(focusedIndex < itemCount - 1 ? focusedIndex + 1 : 0);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex(focusedIndex > 0 ? focusedIndex - 1 : itemCount - 1);
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(itemCount - 1);
          break;
        case 'Tab':
          // Close menu on tab and let focus move naturally
          setIsOpen(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen, focusedIndex, setFocusedIndex, itemCount, triggerRef]);

  // Set initial focus when opening
  React.useEffect(() => {
    if (isOpen) {
      setFocusedIndex(0);
    }
  }, [isOpen, setFocusedIndex]);

  // Prevent body scroll when open
  React.useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Alignment classes
  const alignClasses: Record<DropdownAlign, string> = {
    start: 'left-0',
    center: 'left-1/2 -translate-x-1/2',
    end: 'right-0',
  };

  // Side classes
  const sideClasses: Record<DropdownSide, string> = {
    top: 'bottom-full mb-1',
    bottom: 'top-full mt-1',
  };

  // Animation classes based on side
  const animationClasses = side === 'top'
    ? 'animate-dropdown-enter-up'
    : 'animate-dropdown-enter-down';

  return (
    <div
      ref={contentRef}
      id={menuId}
      role="menu"
      aria-orientation="vertical"
      tabIndex={-1}
      className={[
        'absolute z-50',
        'min-w-[160px] max-w-[320px]',
        'py-1',
        'bg-secondary-100',
        'border border-secondary-200',
        'rounded-lg',
        'shadow-lg',
        alignClasses[align],
        sideClasses[side],
        animationClasses,
        'motion-reduce:animate-none',
        className,
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

DropdownContent.displayName = 'DropdownMenu.Content';

/**
 * DropdownMenu Item component - individual menu item
 */
function DropdownItem({
  children,
  icon,
  onSelect,
  disabled = false,
  destructive = false,
  closeOnSelect: closeOnSelectProp,
  className = '',
}: Readonly<DropdownItemProps>) {
  const { setIsOpen, closeOnSelect: closeOnSelectContext } = useDropdownContext();
  const shouldCloseOnSelect = closeOnSelectProp ?? closeOnSelectContext;

  const handleClick = () => {
    if (disabled) return;
    onSelect?.();
    if (shouldCloseOnSelect) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.();
      if (shouldCloseOnSelect) {
        setIsOpen(false);
      }
    }
  };

  const baseClasses = [
    'flex items-center',
    'w-full',
    'h-9',
    'px-3',
    'text-sm',
    'cursor-pointer',
    'outline-none',
    'transition-colors duration-150 ease-out',
  ];

  const colorClasses = destructive
    ? [
        'text-error-700',
        'hover:bg-error-50',
        'focus:bg-error-50',
      ]
    : [
        'text-secondary-700',
        'hover:bg-secondary-100',
        'focus:bg-secondary-100',
      ];

  const disabledClasses = disabled
    ? ['opacity-50', 'cursor-not-allowed', 'hover:bg-transparent', 'focus:bg-transparent']
    : [];

  return (
    <div
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
      className={[
        ...baseClasses,
        ...colorClasses,
        ...disabledClasses,
        className,
      ].filter(Boolean).join(' ')}
    >
      {icon && (
        <span className="flex-shrink-0 mr-2 w-4 h-4 flex items-center justify-center">
          {icon}
        </span>
      )}
      <span className="truncate">{children}</span>
    </div>
  );
}

DropdownItem.displayName = 'DropdownMenu.Item';

/**
 * DropdownMenu Divider component - separator line between items
 * Uses semantic <hr> element which has implicit role="separator"
 */
function DropdownDivider({ className = '' }: Readonly<DropdownDividerProps>) {
  return (
    <hr
      className={[
        'h-px',
        'my-1',
        'mx-2',
        'bg-secondary-100',
        'border-0',
        className,
      ].filter(Boolean).join(' ')}
    />
  );
}

DropdownDivider.displayName = 'DropdownMenu.Divider';

/**
 * DropdownMenu component for creating accessible dropdown menus with keyboard navigation.
 *
 * Designed for insurance industry applications where quick access to contextual actions
 * is essential for efficient workflow management.
 *
 * Features:
 * - Compound component pattern (Trigger, Content, Item, Divider)
 * - Full keyboard navigation (Arrow keys, Enter, Escape)
 * - Click outside to close
 * - Configurable alignment and side positioning
 * - Support for icons in items
 * - Destructive item styling for dangerous actions
 * - Disabled item support
 * - WCAG accessible with proper ARIA attributes
 *
 * @example
 * // Basic dropdown
 * <DropdownMenu>
 *   <DropdownMenu.Trigger>Options</DropdownMenu.Trigger>
 *   <DropdownMenu.Content>
 *     <DropdownMenu.Item onSelect={() => console.log('Edit')}>Edit</DropdownMenu.Item>
 *     <DropdownMenu.Item onSelect={() => console.log('Duplicate')}>Duplicate</DropdownMenu.Item>
 *     <DropdownMenu.Divider />
 *     <DropdownMenu.Item destructive onSelect={() => console.log('Delete')}>Delete</DropdownMenu.Item>
 *   </DropdownMenu.Content>
 * </DropdownMenu>
 *
 * @example
 * // With icons
 * <DropdownMenu>
 *   <DropdownMenu.Trigger>Actions</DropdownMenu.Trigger>
 *   <DropdownMenu.Content align="end">
 *     <DropdownMenu.Item icon={<Edit />} onSelect={handleEdit}>Edit Policy</DropdownMenu.Item>
 *     <DropdownMenu.Item icon={<Copy />} onSelect={handleCopy}>Duplicate</DropdownMenu.Item>
 *     <DropdownMenu.Divider />
 *     <DropdownMenu.Item icon={<Trash />} destructive onSelect={handleDelete}>Cancel Policy</DropdownMenu.Item>
 *   </DropdownMenu.Content>
 * </DropdownMenu>
 */
const DropdownMenuRoot: React.FC<DropdownMenuProps> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [menuId] = React.useState(() => generateDropdownId());

  const contextValue = React.useMemo<DropdownContextValue>(
    () => ({
      isOpen,
      setIsOpen,
      triggerRef,
      contentRef,
      focusedIndex,
      setFocusedIndex,
      closeOnSelect: true,
      menuId,
    }),
    [isOpen, setIsOpen, triggerRef, contentRef, focusedIndex, setFocusedIndex, menuId]
  );

  return (
    <DropdownContext.Provider value={contextValue}>
      <div className="relative inline-block">{children}</div>
    </DropdownContext.Provider>
  );
};

DropdownMenuRoot.displayName = 'DropdownMenu';

/**
 * DropdownMenu compound component with Trigger, Content, Item, and Divider
 */
export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Trigger: DropdownTrigger,
  Content: DropdownContent,
  Item: DropdownItem,
  Divider: DropdownDivider,
});
